/**
 * Theme Context Provider
 * 
 * Manages application theme state (light, dark, auto). Handles theme persistence
 * in localStorage and user preferences, applies theme to document, and provides
 * effective theme resolution for auto mode based on system preferences.
 * 
 * Uses SSR-safe DOM utilities for document manipulation.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  setDocumentAttribute,
  getSystemColorScheme,
  watchMediaQuery,
  getLocalStorageItem,
  setLocalStorageItem,
  isBrowser,
} from '@/shared/utils/dom/domUtils.js';
import logger from '@/shared/utils/logger.js';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('Light');
  const [isInitialized, setIsInitialized] = useState(false);

  const getEffectiveTheme = useCallback(() => {
    if (theme === 'auto' || theme === 'Auto') {
      const systemScheme = getSystemColorScheme();
      return systemScheme ? systemScheme.toLowerCase() : 'light';
    }
    return (theme || 'light').toLowerCase();
  }, [theme]);

  const applyTheme = useCallback((themeToApply) => {
    const normalizeTheme = themeToApply ? (themeToApply.charAt(0).toUpperCase() + themeToApply.slice(1).toLowerCase()) : 'Light';
    const effectiveTheme = normalizeTheme === 'Auto' 
      ? (getSystemColorScheme() || 'Light')
      : normalizeTheme;

    setDocumentAttribute('data-theme', effectiveTheme.toLowerCase());
  }, []);

  const initializeTheme = useCallback(() => {
    if (!isBrowser()) {
      setIsInitialized(true);
      return;
    }

    try {
      const userDataStr = getLocalStorageItem('userData', '{}');
      const userData = JSON.parse(userDataStr);
      const savedTheme = userData.preferences?.theme || 'Light';
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } catch (error) {
      logger.error('Error loading theme', { error });
      setTheme('Light');
      applyTheme('Light');
    } finally {
      setIsInitialized(true);
    }
  }, [applyTheme]);

  const updateTheme = useCallback((newTheme) => {
    const normalizeTheme = newTheme ? (newTheme.charAt(0).toUpperCase() + newTheme.slice(1).toLowerCase()) : 'Light';
    if (!['Light', 'Dark', 'Auto'].includes(normalizeTheme)) {
      logger.warn('Invalid theme', { theme: newTheme });
      return;
    }

    if (!isBrowser()) {return;}

    setTheme(normalizeTheme);
    applyTheme(normalizeTheme);

    try {
      const userDataStr = getLocalStorageItem('userData', '{}');
      const userData = JSON.parse(userDataStr);
      if (!userData.preferences) {
        userData.preferences = {};
      }
      userData.preferences.theme = newTheme;
      setLocalStorageItem('userData', JSON.stringify(userData));
    } catch (error) {
      logger.error('Error saving theme', { error });
    }
  }, [applyTheme]);

  useEffect(() => {
    const isAutoTheme = theme === 'Auto' || theme === 'auto';
    if (isAutoTheme && isInitialized && isBrowser()) {
      const cleanup = watchMediaQuery('(prefers-color-scheme: dark)', () => {
        applyTheme('Auto');
      });
      
      return cleanup;
    }
  }, [theme, isInitialized, applyTheme]);

  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  useEffect(() => {
    if (isInitialized) {
      applyTheme(theme);
    }
  }, [theme, isInitialized, applyTheme]);

  const value = {
    theme,
    effectiveTheme: getEffectiveTheme(),
    updateTheme,
    isInitialized,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

